/**
 * IssuerPage — public, auth-free landing page for Oak AI Campus's
 * Open Badges issuer profile. Discovered via:
 *   • /sitemap.xml (priority 0.8)
 *   • /.well-known/openbadges (machine-readable)
 *   • LinkedIn / X cert shares deep-link here when readers click "Verify issuer"
 *   • Google + AI engines crawl the page directly
 *
 * Renders both human-readable trust signals AND a `<script type="application/ld+json">`
 * block so any indexer + AI engine extracts the issuer metadata.
 */
import { Link } from "react-router-dom";
import { ShieldCheck, Award, Globe2, ExternalLink, Cloud, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OakLogo from "@/components/OakLogo";

export default function IssuerPage() {
  const issuerJsonLd = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "@id": "https://oakaiapp.com/issuer",
    name: "Oak AI Campus",
    url: "https://oakaiapp.com/",
    logo: "https://oakaiapp.com/oak-icon-512.png",
    email: "info@oakaiapp.com",
    description:
      "Oak AI Campus is an AI-guided mastery learning platform that issues verifiable Open Badges 2.0 + 3.0 (W3C Verifiable Credentials) digital credentials. Owned and operated by Digiluxcon Technology Services (OPC) Private Limited, India.",
    parentOrganization: {
      "@type": "Organization",
      name: "Digiluxcon Technology Services (OPC) Private Limited",
      address: {
        "@type": "PostalAddress",
        addressCountry: "IN",
        addressLocality: "Mumbai",
      },
    },
    knowsAbout: [
      "Open Badges 2.0",
      "Open Badges 3.0",
      "W3C Verifiable Credentials",
      "AI-Guided Learning",
      "Adaptive Assessment",
    ],
    award: "Verifiable Open Badges 2.0 + 3.0 Digital Credentials",
    sameAs: ["https://x.com/OakAICampus", "https://www.linkedin.com/company/oak-ai-campus/"],
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(issuerJsonLd) }}
      />
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8" data-testid="issuer-page">
        <div className="flex items-center gap-4">
          <OakLogo size={56} glow />
          <div>
            <div className="label-eyebrow">Open Badges Issuer Profile</div>
            <h1 className="font-display text-3xl sm:text-5xl tracking-tight mt-1">
              Oak AI Campus
            </h1>
            <p className="text-white/55 text-sm mt-1">
              An accredited issuer of verifiable digital credentials, operated by{" "}
              <span className="text-white/85">
                Digiluxcon Technology Services (OPC) Private Limited
              </span>
              .
            </p>
          </div>
        </div>

        <section
          className="rounded-2xl border border-[#00FF9D]/22 bg-gradient-to-br from-[#00FF9D]/[0.06] to-[#0044FF]/[0.04] p-6 sm:p-7"
          data-testid="issuer-trust-summary"
        >
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} className="text-[#9bffcc]" />
            <h2 className="font-display text-xl tracking-tight">Verifiable Credential Issuer</h2>
          </div>
          <p className="text-white/75 text-[14.5px] leading-relaxed">
            Every Oak AI Campus certificate ships as a cryptographically-attestable Open Badge —
            both 2.0 (legacy JSON-LD) and 3.0 (W3C Verifiable Credentials format). Any compliant
            wallet (Mozilla Backpack, Velocity, MyCreds), inspector tool (1EdTech Validator,
            Digital Credentials Inspector), or ATS system can verify a credential by hitting the
            machine-readable endpoints below — no Oak AI Campus account required.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EndpointCard
            icon={<Award size={14} />}
            title="Open Badges 2.0"
            url="/api/badges/issuer"
            kind="Issuer Profile"
            testid="ep-ob2-issuer"
          />
          <EndpointCard
            icon={<Award size={14} />}
            title="Open Badges 3.0"
            url="/api/badges/issuer/v3"
            kind="W3C VC Issuer"
            testid="ep-ob3-issuer"
          />
          <EndpointCard
            icon={<Globe2 size={14} />}
            title=".well-known"
            url="/.well-known/openbadges"
            kind="Auto-discovery"
            testid="ep-well-known"
          />
          <EndpointCard
            icon={<Globe2 size={14} />}
            title="Sitemap"
            url="/sitemap.xml"
            kind="All credential URLs"
            testid="ep-sitemap"
          />
        </section>

        <section
          className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-3"
          data-testid="issuer-verification"
        >
          <h3 className="font-display text-lg tracking-tight">How to verify a credential</h3>
          <ol className="list-decimal ml-5 text-[13.5px] text-white/70 space-y-1.5 leading-relaxed">
            <li>
              Find the credential ID (e.g. <code className="font-mono-oak text-white/85">OAK-2D81A599</code>)
              on the certificate.
            </li>
            <li>
              Fetch{" "}
              <code className="font-mono-oak text-white/85">
                https://oakaiapp.com/api/badges/{"{cert_id}"}/v3.json
              </code>{" "}
              (Open Badges 3.0) or{" "}
              <code className="font-mono-oak text-white/85">
                https://oakaiapp.com/api/badges/{"{cert_id}"}.json
              </code>{" "}
              (Open Badges 2.0).
            </li>
            <li>
              Confirm the <code className="font-mono-oak text-white/85">issuer</code> field
              resolves to{" "}
              <code className="font-mono-oak text-white/85">
                https://oakaiapp.com/api/badges/issuer
              </code>{" "}
              and matches the recipient's hashed identity claim.
            </li>
            <li>
              Optional: paste the assertion URL into the{" "}
              <a
                href="https://validator.imsglobal.org/openbadges3/credentialvalidation"
                target="_blank"
                rel="noreferrer"
                className="text-[#8aa8ff] inline-flex items-center gap-1"
              >
                1EdTech Open Badges Validator <ExternalLink size={11} />
              </a>{" "}
              for an external second opinion.
            </li>
          </ol>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="issuer-infra">
          <InfraCard icon={<Cloud size={14} />} title="Infrastructure" body="AWS Mumbai (ap-south-1) · MongoDB Atlas" />
          <InfraCard icon={<Sparkles size={14} />} title="AI Backend" body="Google Gemini API for path generation + grading" />
          <InfraCard icon={<ShieldCheck size={14} />} title="Compliance" body="DPDPA 2023, GDPR, COPPA-aware (under-18 VPC)" />
        </section>

        <div className="text-center">
          <Link
            to="/"
            className="text-[#8aa8ff] hover:text-white text-sm inline-flex items-center gap-1.5"
            data-testid="issuer-back-home"
          >
            ← Back to Oak AI Campus
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function EndpointCard({ icon, title, url, kind, testid }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="group rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-[#00FF9D]/30 hover:bg-[#00FF9D]/[0.04] transition flex items-center gap-3"
      data-testid={testid}
    >
      <span className="rounded-lg bg-white/[0.05] p-2 text-white/85">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-display font-semibold text-white">{title}</div>
        <div className="text-[10.5px] text-white/45 tracking-[0.18em] uppercase mt-0.5">
          {kind}
        </div>
        <code className="text-[11px] text-[#8aa8ff] truncate block mt-0.5 font-mono-oak">
          {url}
        </code>
      </div>
      <ExternalLink size={13} className="text-white/30 group-hover:text-[#9bffcc] transition" />
    </a>
  );
}

function InfraCard({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-display tracking-[0.18em] uppercase text-white/55">
        {icon} {title}
      </div>
      <div className="text-[12.5px] text-white/75 mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}
